/* eslint-disable no-shadow */
import React from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { format, startOfDay } from 'date-fns';

import { Avatar } from 'uiToolkitV1/Avatar';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

// eslint-disable-next-line import/no-cycle
import DescriptionText from '../components/DescriptionText';
import { PraiseAdvice } from '../components/common';

export const TextContainer = styled(Flex)`
  position: relative;
  min-height: 40px;
`;
export const TruncatedTextBodyMedium = styled(TextBodyMedium)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-break: 'break-word';
  overflow: hidden;
  // visibility: visible;

  // ${TextContainer}:hover & {
  //   visibility: hidden;
  // }
`;

const FEEDBACK_TYPE = {
  CONSTRUCTIVE: 'Feedback',
  APPRECIATIVE: 'Praise',
};

export const CF_TABLE_META_CELLS = {
  DATE: ({ displayName, id }) => ({
    headerName: displayName,
    colId: id,
    filter: 'agDateColumnFilter',
    sortable: true,
    maxWidth: 200,
    valueGetter: (params) => params?.data?.date.value,
    filterValueGetter: ({ data }) => {
      const { value } = data.date ?? {};
      return startOfDay(new Date(value));
    },
    cellRenderer: (props) => {
      const { value } = props.data.date ?? {};
      return (
        <Flex w="100%" h="100%" alignItems="center">
          <TextBodyMedium>{format(new Date(value), 'PP')}</TextBodyMedium>
        </Flex>
      );
    },
  }),
  DESCRIPTION_TEXT: ({ displayName, id, type, hideViewFullComment }) => ({
    headerName: displayName,
    colId: id,
    filter: true,
    sortable: true,
    flex: 1,
    minWidth: 480,
    valueGetter: ({ data }) => {
      const { value } = data.requestDetails ?? data.comment;
      return value.text;
    },
    cellRenderer: (props) => (
      <Flex w="100%" h="100%" alignItems="center">
        <DescriptionText type={type} hideViewFullComment={hideViewFullComment} {...props} />
      </Flex>
    ),
  }),
  EMPLOYEE_NAME: ({ displayName, id }) => ({
    colId: id,
    field: id,
    headerName: displayName,
    width: 250,
    sortable: true,
    pinned: 'left',
    filter: 'agSetColumnFilter',
    filterValueGetter: ({ data }) => data?.[id].value.displayName,
    valueGetter: ({ data }) => data?.[id].value.displayName,
    cellRenderer: ({ data }) => {
      const { value, imageUrl } = data?.[id] ?? {};
      const { requestedByEmployee } = data;
      // eslint-disable-next-line no-shadow
      const { displayName } = value ?? {};
      const { metadata } = requestedByEmployee ?? {};
      const { displayName: requestedByDisplayName } = metadata ?? {};
      return (
        <Flex w="100%" h="100%" alignItems="center">
          <Avatar name={displayName} src={imageUrl} />
          <Flex flexDirection="column" sx={{ marginLeft: '8px' }}>
            <TextBodyMedium>{displayName}</TextBodyMedium>
            {requestedByDisplayName && <TextLabel color="text.g2">Requested by {requestedByDisplayName}</TextLabel>}
          </Flex>
        </Flex>
      );
    },
  }),
  MULTIPLE_EMPLOYEES: ({ displayName, id }) => ({
    headerName: displayName,
    colId: id,
    filter: 'agSetColumnFilter',
    sortable: true,
    maxwidth: 250,
    pinned: 'left',
    filterValueGetter: ({ data }) => {
      const { value } = data?.employee ?? {};
      const { employeeMiniProfiles } = value ?? {};
      const displayName = employeeMiniProfiles?.map((emp) => emp?.displayName);
      return displayName[0];
    },
    valueGetter: ({ data }) => {
      const { value } = data?.employee ?? {};
      const { employeeMiniProfiles } = value ?? {};
      const displayName = employeeMiniProfiles?.map((emp) => emp?.displayName);
      return displayName;
    },
    cellRenderer: (props) => {
      const { value } = props.data.employee ?? {};
      const { employeeMiniProfiles, requesterEmployee } = value ?? {};

      if (employeeMiniProfiles?.length === 1) {
        return (
          <Flex w="100%" h="100%" alignItems="center">
            {employeeMiniProfiles?.slice(0, 3)?.map(({ displayName: employeeName, id, profilePhoto }) => (
              <Avatar name={employeeName} src={profilePhoto} key={id} />
            ))}
            <Flex flexDirection="column" sx={{ marginLeft: '8px' }}>
              <TextBodyMedium>{employeeMiniProfiles[0]?.displayName}</TextBodyMedium>
              {/* {requesterEmployee && (
                <TextLabel color="text.g2">Requested by {requesterEmployee?.displayName}</TextLabel>
              )} */}
            </Flex>
          </Flex>
        );
      }
      return (
        <Flex w="100%" h="100%" alignItems="center">
          {employeeMiniProfiles?.slice(0, 3)?.map(({ displayName: employeeName, id, profilePhoto }) => (
            <Avatar sx={{ marginRight: '1px' }} name={employeeName} src={profilePhoto} key={id} />
          ))}
          {employeeMiniProfiles && employeeMiniProfiles.length > 3 && (
            <TextBodyMedium ml="4px" color="text.g1">
              +{employeeMiniProfiles.length - 3}
            </TextBodyMedium>
          )}
          <Flex flexDirection="column" sx={{ marginLeft: '8px' }}>
            {requesterEmployee && <TextLabel color="text.g2">Requested by {requesterEmployee.displayName}</TextLabel>}
          </Flex>
        </Flex>
      );
    },
  }),
  FEEDBACK_TYPE: ({ displayName, id }) => ({
    headerName: displayName,
    colId: id,
    filter: 'agSetColumnFilter',
    sortable: true,
    maxWidth: 200,
    valueGetter: ({ data }) => {
      const { value } = data.feedbackType;
      return FEEDBACK_TYPE[value];
    },
    cellRenderer: (props) => {
      const { value } = props.data.feedbackType ?? {};
      return (
        <Flex w="100%" h="100%" alignItems="center">
          <PraiseAdvice value={value} />
        </Flex>
      );
    },
  }),
  MULTIPLE_EMPLOYEES_2: ({ displayName, id }) => ({
    headerName: displayName,
    colId: id,
    filter: 'agSetColumnFilter',
    sortable: true,
    maxwidth: 250,
    filterValueGetter: ({ data }) => {
      const receivedEmployeeIds = Object.keys(data.comment.value.macros.employees);
      const receivedEmployees = receivedEmployeeIds.map((id) => data.comment.value.macros.employees[id]);
      const displayName = receivedEmployees?.map((emp) => emp?.metadata?.displayName);
      return displayName;
    },
    valueGetter: ({ data }) => {
      const receivedEmployeeIds = Object.keys(data.comment.value.macros.employees);
      const receivedEmployees = receivedEmployeeIds.map((id) => data.comment.value.macros.employees[id]);
      const displayName = receivedEmployees?.map((emp) => emp?.metadata?.displayName);
      return displayName;
    },
    cellRenderer: (props) => {
      const receivedEmployeeIds = Object.keys(props.data.comment.value.macros.employees);
      const employeeMiniProfiles = receivedEmployeeIds.map((id) => props.data.comment.value.macros.employees[id]);
      if (employeeMiniProfiles?.length === 1) {
        return (
          <Flex w="100%" h="100%" alignItems="center">
            <Avatar
              name={employeeMiniProfiles[0]?.metadata?.displayName}
              src={employeeMiniProfiles[0]?.profile?.profilePhoto}
              key={id}
            />
            <Flex flexDirection="column" sx={{ marginLeft: '8px' }}>
              <TextBodyMedium>{employeeMiniProfiles[0]?.metadata?.displayName}</TextBodyMedium>
            </Flex>
          </Flex>
        );
      }
      return (
        <Flex w="100%" h="100%" alignItems="center">
          {employeeMiniProfiles?.slice(0, 3)?.map((employee) => (
            <Avatar
              sx={{ marginRight: '1px' }}
              name={employee?.metadata?.displayName}
              src={employee?.profile?.profilePhoto}
              key={id}
            />
          ))}
          {employeeMiniProfiles && employeeMiniProfiles.length > 3 && (
            <TextBodyMedium ml="4px" color="text.g1">
              +{employeeMiniProfiles.length - 3}
            </TextBodyMedium>
          )}
        </Flex>
      );
    },
  }),
};
